import {
  importRosterDisabledReason,
  shouldAllowAddImportRoster,
  shouldAllowAddPlayer,
  shouldAllowAddTeamOfficials,
} from '@sportninja/common/ts-utils/add-roster-utils'
import type { OrganizationDetails } from '@sportninja/common/services/organization/readOrganization'
import { isRosterLockPassed } from '@sportninja/common/features/competition/operations/utils/rosterLock'
import { useRosterContext } from 'src/pages/Team/UnifiedRoster/context/RosterContext'

/**
 * The two Add decisions the panel header needs, computed inside the provider,
 * plus whether the roster the decisions depend on is still loading. `false`
 * on `canAddPlayer`/`canAddOfficial` is ambiguous on its own — it means both
 * "loading" and "denied" — so callers that need to tell those apart (the
 * header's Add button) must read `isLoading` alongside them.
 */
export interface RosterCapabilities {
  canAddPlayer: boolean
  canAddOfficial: boolean
  canImportRoster: boolean
  /** Tooltip text when import is denied; null when it is allowed. */
  importDisabledReason: string | null
  canExportRoster: boolean
  /** Both CSV endpoints are roster-scoped, so neither button works without it. */
  rosterId: string | null
  isLoading: boolean
}

/**
 * The pre-report state: every gate closed, `isLoading` true. Callers use this
 * both for initial state and to reset on selection change, so the new fields
 * cannot be forgotten in one place and set in another.
 */
export const LOADING_ROSTER_CAPABILITIES: RosterCapabilities = {
  canAddPlayer: false,
  canAddOfficial: false,
  canImportRoster: false,
  importDisabledReason: null,
  canExportRoster: false,
  rosterId: null,
  isLoading: true,
}

export interface RosterPermissions extends RosterCapabilities {
  /** Row actions and inline editing. */
  canEditRoster: boolean
  /** Forwarded to the player modal as `hasAdminPermission`. */
  canAdmin: boolean
}

interface Args {
  /** Competition-level permission, prop-drilled from the Schedule page. */
  canEdit: boolean
  /** Null until the organization request resolves. */
  organization: OrganizationDetails | null
}

/**
 * Answers every roster permission question for the Operations tab, using the
 * SAME helpers as the Team screen so the two surfaces cannot diverge.
 *
 * The team-level flags come from `meta.permissions[teamId]` on the roster
 * players response, which `RosterProvider` already extracts into context.
 * `canEdit` stays an outer AND throughout: holding the competition permission
 * is necessary but no longer sufficient.
 */
export const useRosterPermissions = ({
  canEdit,
  organization,
}: Args): RosterPermissions => {
  'use memo'

  const {
    permissions,
    selectedRosterId,
    lockRosterDate,
    canAddPlayersFromRoster,
    isRegistrationOn,
    loading,
  } = useRosterContext()

  const adminPermission = Boolean(permissions?.admin)
  const updatePermission = Boolean(permissions?.update)

  // Same rule as the Team screen and the panel's roster lock pill.
  const isCurrentDateAfterDivisionOrCompetitionLockDate =
    isRosterLockPassed(lockRosterDate)

  const shared = {
    adminPermission,
    updatePermission,
    rosterId: selectedRosterId ?? '',
    sync_salesforce: Boolean(organization?.sync_salesforce),
    // The helpers only read `organization?.id`; an unresolved org therefore
    // errs closed for non-admins rather than opening the gate.
    organization: organization as never,
  }

  // Same inputs feed the allow decision and its explanation, so the tooltip
  // can never describe a different state than the one that disabled it.
  const importArgs = {
    ...shared,
    isCurrentDateAfterDivisionOrCompetitionLockDate,
    canAddPlayersFromRoster: Boolean(canAddPlayersFromRoster),
    isRegistrationOn: Boolean(isRegistrationOn),
  }
  const canImportRoster = canEdit && shouldAllowAddImportRoster(importArgs)

  return {
    canEditRoster: canEdit && (updatePermission || adminPermission),
    canAdmin: adminPermission,
    canAddPlayer:
      canEdit &&
      shouldAllowAddPlayer({
        ...shared,
        isCurrentDateAfterDivisionOrCompetitionLockDate,
        canAddPlayersFromRoster: Boolean(canAddPlayersFromRoster),
        isRegistrationOn: Boolean(isRegistrationOn),
      }),
    canAddOfficial: canEdit && shouldAllowAddTeamOfficials({ ...shared }),
    canImportRoster,
    importDisabledReason: importRosterDisabledReason({
      ...importArgs,
      isAllowed: canImportRoster,
    }),
    // Deliberately `update || admin`, unlike the Team screen's export gate,
    // which checks `update` alone and so hides Export from an admin who can
    // still Import. Export is the strictly safer of the two.
    canExportRoster:
      canEdit &&
      (updatePermission || adminPermission) &&
      Boolean(selectedRosterId),
    rosterId: selectedRosterId ?? null,
    isLoading: Boolean(loading),
  }
}
